import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ClockIcon,
  CalendarDaysIcon,
  BuildingStorefrontIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
  BookOpenIcon,
  AcademicCapIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  FolderIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import localStorageService from '../../utils/localStorageService';

const ExpenseTracker = () => {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: '',
    supplier: '',
    paymentMethod: 'cash',
    date: new Date().toISOString().split('T')[0]
  });

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setExpenseForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmitExpense = (e) => {
    e.preventDefault();
    
    if (!expenseForm.description || !expenseForm.amount || !expenseForm.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        id: Date.now().toString(),
        status: 'completed',
        createdAt: new Date().toISOString()
      };

      // Save to localStorage (you could create an 'expenses' collection)
      const existingExpenses = localStorageService.getAllItems('expenses') || [];
      existingExpenses.push(expenseData);
      localStorage.setItem('expenses', JSON.stringify(existingExpenses));

      // Reset form and close modal
      setExpenseForm({
        description: '',
        amount: '',
        category: '',
        supplier: '',
        paymentMethod: 'cash',
        date: new Date().toISOString().split('T')[0]
      });
      setShowAddExpenseModal(false);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries('expenses');
      
      toast.success('Dépense ajoutée avec succès !');
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Erreur lors de l\'ajout de la dépense');
    }
  };

  // Reset form when modal closes
  const closeModal = () => {
    setExpenseForm({
      description: '',
      amount: '',
      category: '',
      supplier: '',
      paymentMethod: 'cash',
      date: new Date().toISOString().split('T')[0]
    });
    setShowAddExpenseModal(false);
  };

  // Fetch real-time data from localStorage
  const { data: trainersData, isLoading: trainersLoading } = useQuery(
    'trainers',
    () => {
      try {
        const trainers = localStorageService.getAllItems('trainers');
        return { trainers };
      } catch (error) {
        console.error('Error fetching trainers:', error);
        return { trainers: [] };
      }
    },
    {
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery(
    'training-sessions',
    () => {
      try {
        const sessions = localStorageService.getAllItems('training-sessions');
        return { sessions };
      } catch (error) {
        console.error('Error fetching training sessions:', error);
        return { sessions: [] };
      }
    },
    {
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  const { data: classroomsData, isLoading: classroomsLoading } = useQuery(
    'classrooms',
    () => {
      try {
        const classrooms = localStorageService.getAllItems('classrooms');
        return { classrooms };
      } catch (error) {
        console.error('Error fetching classrooms:', error);
        return { classrooms: [] };
      }
    },
    {
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Fetch saved expenses from localStorage
  const { data: savedExpensesData, isLoading: expensesLoading } = useQuery(
    'expenses',
    () => {
      try {
        const expenses = JSON.parse(localStorage.getItem('expenses') || '[]');
        return { expenses };
      } catch (error) {
        console.error('Error fetching expenses:', error);
        return { expenses: [] };
      }
    },
    {
      staleTime: 1 * 60 * 1000 // 1 minute
    }
  );

  // Calculate real expenses from saved data
  const expenseCalculations = React.useMemo(() => {
    const savedExpenses = savedExpensesData?.expenses || [];
    
    // Calculate expenses by category from actual saved data
    const salariesExpense = savedExpenses
      .filter(exp => exp.category === 'salaries')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const facilityCosts = savedExpenses
      .filter(exp => exp.category === 'rent')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const sessionCosts = savedExpenses
      .filter(exp => exp.category === 'equipment')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const utilitiesExpense = savedExpenses
      .filter(exp => exp.category === 'utilities')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const maintenanceExpense = savedExpenses
      .filter(exp => exp.category === 'maintenance')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const marketingExpense = savedExpenses
      .filter(exp => exp.category === 'marketing')
      .reduce((total, exp) => total + (exp.amount || 0), 0);
      
    const otherExpenses = savedExpenses
      .filter(exp => exp.category === 'other')
      .reduce((total, exp) => total + (exp.amount || 0), 0);

    const totalExpenses = salariesExpense + facilityCosts + sessionCosts + utilitiesExpense + 
                         maintenanceExpense + marketingExpense + otherExpenses;

    return {
      salariesExpense,
      facilityCosts,
      sessionCosts,
      utilitiesExpense,
      maintenanceExpense,
      marketingExpense,
      otherExpenses,
      totalExpenses
    };
  }, [savedExpensesData]);

  // Catégories de dépenses éducatives
  const expenseCategories = [
    { id: 'rent', name: 'Loyer salle', icon: BuildingStorefrontIcon, color: 'blue' },
    { id: 'utilities', name: 'Électricité/Eau', icon: LightBulbIcon, color: 'yellow' },
    { id: 'maintenance', name: 'Maintenance', icon: WrenchScrewdriverIcon, color: 'orange' },
    { id: 'equipment', name: 'Matériel pédagogique', icon: BookOpenIcon, color: 'green' },
    { id: 'salaries', name: 'Salaires formateurs', icon: AcademicCapIcon, color: 'purple' },
    { id: 'marketing', name: 'Publicité formations', icon: PaperAirplaneIcon, color: 'red' },
    { id: 'other', name: 'Autres frais', icon: FolderIcon, color: 'gray' }
  ];

  // Calculate real expenses by category from operational data
  const expensesByCategory = React.useMemo(() => {
    const { 
      salariesExpense, 
      facilityCosts, 
      sessionCosts, 
      utilitiesExpense, 
      maintenanceExpense, 
      marketingExpense, 
      otherExpenses, 
      totalExpenses 
    } = expenseCalculations;

    return [
    { 
      category: 'rent',
      amount: facilityCosts, 
      percentage: totalExpenses > 0 ? Math.round((facilityCosts / totalExpenses) * 100) : 0,
      trend: facilityCosts > 0 ? 'Données réelles' : '',
      description: 'Location et coûts des salles'
    },
    { 
      category: 'salaries', 
      amount: salariesExpense, 
      percentage: totalExpenses > 0 ? Math.round((salariesExpense / totalExpenses) * 100) : 0,
      trend: salariesExpense > 0 ? 'Données réelles' : '',
      description: 'Salaires des formateurs'
    },
    { 
      category: 'utilities', 
      amount: utilitiesExpense, 
      percentage: totalExpenses > 0 ? Math.round((utilitiesExpense / totalExpenses) * 100) : 0, 
      trend: utilitiesExpense > 0 ? 'Données réelles' : '',
      description: 'Électricité, eau, internet'
    },
    { 
      category: 'maintenance', 
      amount: maintenanceExpense, 
      percentage: totalExpenses > 0 ? Math.round((maintenanceExpense / totalExpenses) * 100) : 0, 
      trend: maintenanceExpense > 0 ? 'Données réelles' : '',
      description: 'Réparation et entretien'
    },
    { 
      category: 'equipment', 
      amount: sessionCosts, 
      percentage: totalExpenses > 0 ? Math.round((sessionCosts / totalExpenses) * 100) : 0, 
      trend: sessionCosts > 0 ? 'Données réelles' : '',
      description: 'Équipements et matériel'
    },
    { 
      category: 'marketing', 
      amount: marketingExpense, 
      percentage: totalExpenses > 0 ? Math.round((marketingExpense / totalExpenses) * 100) : 0, 
      trend: marketingExpense > 0 ? 'Données réelles' : '',
      description: 'Publicité et promotion'
    },
    { 
      category: 'other', 
      amount: otherExpenses, 
      percentage: totalExpenses > 0 ? Math.round((otherExpenses / totalExpenses) * 100) : 0, 
      trend: otherExpenses > 0 ? 'Données réelles' : '',
      description: 'Dépenses diverses'
    }
  ].filter(item => item.amount > 0); // Only show categories with actual expenses
  }, [expenseCalculations]);

  // Get saved expenses from localStorage
  const allExpenses = React.useMemo(() => {
    const savedExpenses = savedExpensesData?.expenses || [];
    
    // Convert saved expenses to match the expected format
    const formattedSavedExpenses = savedExpenses.map(expense => ({
      id: expense.id,
      date: expense.date,
      category: expense.category,
      amount: expense.amount,
      supplier: expense.supplier || 'Non spécifié',
      description: expense.description,
      paymentMethod: expense.paymentMethod || 'cash',
      status: expense.status || 'completed'
    }));

    // Return only real localStorage data, sorted by date (newest first)
    return formattedSavedExpenses
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [savedExpensesData]);

  const recentExpenses = allExpenses;

  // Calculate real monthly expense history from saved data
  const monthlyExpenseHistory = React.useMemo(() => {
    const savedExpenses = savedExpensesData?.expenses || [];
    const currentYear = new Date().getFullYear();
    
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    
    return months.map((month, index) => {
      const monthExpenses = savedExpenses.filter(expense => {
        const expenseDate = new Date(expense.date || expense.createdAt);
        return expenseDate.getFullYear() === currentYear && 
               expenseDate.getMonth() === index;
      });
      
      const monthTotal = monthExpenses.reduce((total, exp) => total + (exp.amount || 0), 0);
      
      return {
        month,
        amount: monthTotal,
        trend: monthTotal > 0 ? `${monthExpenses.length} dépense(s)` : ''
      };
    }).filter(monthData => monthData.amount > 0); // Only show months with actual expenses
  }, [savedExpensesData]);

  // Calculs des totaux à partir des données réelles
  const totalExpenses = expenseCalculations.totalExpenses;

  // Loading state
  const isLoading = trainersLoading || sessionsLoading || classroomsLoading || expensesLoading;

  // Filtrer les dépenses récentes
  const filteredExpenses = recentExpenses.filter(expense => {
    const matchesSearch = searchTerm === '' || 
      expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  // Formatage des nombres pour l'affichage
  const formatCurrency = (value) => value.toLocaleString('fr-DZ');
  
  // Formatage des dates
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Récupérer l'icône pour une catégorie
  const getCategoryIcon = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    const IconComponent = category ? category.icon : FolderIcon;
    return <IconComponent className={`h-5 w-5 text-${category?.color || 'gray'}-400`} />;
  };

  // Récupérer le nom pour une catégorie
  const getCategoryName = (categoryId) => {
    const category = expenseCategories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Autre';
  };

  // Conversion des périodes
  const periodLabels = {
    'day': 'Aujourd\'hui',
    'week': 'Cette semaine',
    'month': 'Ce mois',
    'quarter': 'Ce trimestre',
    'year': 'Cette année'
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec sélection de période */}
      <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <BanknotesIcon className="h-6 w-6 text-red-400 mr-2" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Suivi des dépenses</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50"
            >
              <option value="01" className="bg-slate-800">Janvier</option>
              <option value="02" className="bg-slate-800">Février</option>
              <option value="03" className="bg-slate-800">Mars</option>
              <option value="04" className="bg-slate-800">Avril</option>
              <option value="05" className="bg-slate-800">Mai</option>
              <option value="06" className="bg-slate-800">Juin</option>
              <option value="07" className="bg-slate-800">Juillet</option>
            </select>
            
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50"
            >
              <option value="2023" className="bg-slate-800">2023</option>
              <option value="2022" className="bg-slate-800">2022</option>
            </select>
            
            <button 
              onClick={() => setShowAddExpenseModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Ajouter dépense</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-300 text-sm">Total Dépenses</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">{formatCurrency(totalExpenses)} DA</h3>
            </div>
            <div className="p-2 bg-red-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-red-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-red-400 text-sm">
              {totalExpenses > 0 ? 'Données réelles' : 'Aucune dépense'}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-300 text-sm">Dépense Principale</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {(() => {
                  const maxExpense = expensesByCategory.reduce((max, item) => 
                    item.amount > max.amount ? item : max, 
                    { amount: 0, description: 'Aucune dépense' }
                  );
                  return maxExpense.description;
                })()}
              </h3>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-purple-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              {(() => {
                const maxExpense = expensesByCategory.reduce((max, item) => 
                  item.amount > max.amount ? item : max, 
                  { amount: 0, percentage: 0 }
                );
                return `${formatCurrency(maxExpense.amount)} DA (${maxExpense.percentage}% du total)`;
              })()}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border border-orange-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-orange-300 text-sm">Mois Actuel</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {(() => {
                  const currentMonth = new Date().getMonth();
                  const currentMonthData = monthlyExpenseHistory.find((_, index) => index === currentMonth);
                  return `${formatCurrency(currentMonthData?.amount || 0)} DA`;
                })()}
              </h3>
            </div>
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-orange-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <span className="text-orange-400 text-sm">
              {(() => {
                const currentMonth = new Date().getMonth();
                const currentMonthData = monthlyExpenseHistory.find((_, index) => index === currentMonth);
                return currentMonthData?.trend || 'Aucune dépense ce mois';
              })()}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Répartition des dépenses par catégorie */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Répartition des dépenses</h2>
        </div>

        <div className="space-y-4">
          {expensesByCategory.map((item, index) => (
            <div key={`expense-category-${item.category}-${index}`} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {getCategoryIcon(item.category)}
                  <div className="ml-3">
                    <h3 className="text-slate-900 dark:text-slate-50 font-medium">{getCategoryName(item.category)}</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-900 dark:text-slate-50 font-semibold">{formatCurrency(item.amount)} DA</p>
                  <p className={`text-sm ${!item.trend.includes('-') ? 'text-red-400' : 'text-green-400'}`}>
                    {item.trend} vs mois précédent
                  </p>
                </div>
              </div>
              <div className="w-full bg-slate-50 dark:bg-slate-800/80 h-2 rounded-full mt-2">
                <div 
                  className={`h-2 rounded-full bg-${expenseCategories.find(cat => cat.id === item.category)?.color || 'gray'}-500/60`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Total Dépenses: {formatCurrency(totalExpenses)} DA</h3>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Période: {month}/{year}</p>
          </div>
        </div>
      </motion.div>
      
      {/* Historique des dépenses mensuelles */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <div className="flex items-center mb-4">
          <CalendarDaysIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Historique des dépenses</h2>
        </div>

        <div className="overflow-x-auto">
          <div className="flex flex-nowrap gap-4 pb-4">
            {monthlyExpenseHistory.map((item, index) => (
              <div 
                key={`expense-filter-${index}`} 
                className={`flex-none w-44 p-4 rounded-lg ${month === String(index + 1).padStart(2, '0') ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-slate-100/80 dark:bg-slate-800/40'}`}
              >
                <h3 className="text-slate-900 dark:text-slate-50 font-medium">{item.month} 2023</h3>
                <p className="text-slate-900 dark:text-slate-50 text-lg font-semibold mt-2">{formatCurrency(item.amount)} DA</p>
                {item.trend && (
                  <div className="flex items-center mt-1">
                    <span className="text-slate-500 dark:text-slate-400 text-sm">
                      {item.trend} 
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Liste des dépenses récentes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center">
            <ClockIcon className="h-6 w-6 text-blue-400 mr-2" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Dépenses récentes</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-white/50"
              />
            </div>
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50"
            >
              <option value="all" className="bg-slate-800">Toutes les catégories</option>
              {expenseCategories.map(cat => (
                <option key={cat.id} value={cat.id} className="bg-slate-800">{cat.name}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredExpenses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Date</th>
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Description</th>
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Catégorie</th>
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Fournisseur</th>
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Montant</th>
                  <th className="pb-3 text-slate-900 dark:text-slate-50 font-medium">Méthode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredExpenses.map((expense, index) => (
                  <tr key={expense.id} className="hover:bg-slate-100/80 dark:bg-slate-800/40 transition-colors">
                    <td className="py-3 text-slate-900 dark:text-slate-50 whitespace-nowrap">{formatDate(expense.date)}</td>
                    <td className="py-3 text-slate-900 dark:text-slate-50">{expense.description}</td>
                    <td className="py-3">
                      <div className="flex items-center">
                        {getCategoryIcon(expense.category)}
                        <span className="ml-2 text-slate-900 dark:text-slate-50">{getCategoryName(expense.category)}</span>
                      </div>
                    </td>
                    <td className="py-3 text-slate-900 dark:text-slate-50">{expense.supplier}</td>
                    <td className="py-3 text-slate-900 dark:text-slate-50 font-medium">{formatCurrency(expense.amount)} DA</td>
                    <td className="py-3 text-slate-900 dark:text-slate-50">{expense.paymentMethod}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg">
            <BanknotesIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto mb-4" />
            <p className="text-slate-900 dark:text-slate-50 font-medium">Aucune dépense trouvée</p>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Essayez de modifier vos filtres ou d'ajouter une nouvelle dépense</p>
          </div>
        )}
      </motion.div>

      {/* Modal d'ajout de dépense */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 z-50 flex items-start justify-center overflow-y-auto">
          <div className="w-full max-w-lg p-4 pt-8 pb-8">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
              className="bg-gray-800 rounded-xl p-6 border border-gray-600 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Ajouter une dépense</h2>
              <button
                onClick={closeModal}
                className="p-1 hover:bg-gray-600 rounded-lg transition-colors text-gray-300 hover:text-slate-900 dark:text-slate-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitExpense} className="space-y-4">
              {/* Description */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  name="description"
                  value={expenseForm.description}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Facture électricité, achat équipement..."
                  required
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Montant (DA) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={expenseForm.amount}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Catégorie *
                </label>
                <select
                  name="category"
                  value={expenseForm.category}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {expenseCategories.map(cat => (
                    <option key={cat.id} value={cat.id} className="bg-slate-800">{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Fournisseur
                </label>
                <input
                  type="text"
                  name="supplier"
                  value={expenseForm.supplier}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Nom du fournisseur ou prestataire"
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Méthode de paiement
                </label>
                <select
                  name="paymentMethod"
                  value={expenseForm.paymentMethod}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="cash" className="bg-slate-800">Espèces</option>
                  <option value="card" className="bg-slate-800">Carte bancaire</option>
                  <option value="transfer" className="bg-slate-800">Virement</option>
                  <option value="cheque" className="bg-slate-800">Chèque</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={expenseForm.date}
                  onChange={handleFormChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-slate-900 dark:text-slate-50 rounded-lg transition-colors border border-gray-500"
                >
                  Annuler
                </button>
              <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                  Ajouter dépense
              </button>
            </div>
            </form>
          </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTracker; 